import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  useGetMtoMetricsQuery,
  useGetMtoNomenclatureQuery,
  useImportMtoNomenclatureMutation,
  useListMtoNomenclatureItemsQuery,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { apiErrorMessage, type MtoMetrics } from '@entities/mto';
import { Card, CardTitle } from '@shared/ui/Card';
import { Input } from '@shared/ui/Input';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { ACCEPT_ANY } from '@shared/lib/fileAccept';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { IconArrowRight, IconChevronDown, IconChevronRight, IconClipboardCheck } from '@shared/ui/icons';

const SectionLabel = styled.div`
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(8)};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const StatLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/* ── Справочник НСИ (модуль 1.3) ───────────────────────────────────────────── */

const NsiCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const NsiHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const NsiCount = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing(2)};
`;

const NsiCountValue = styled.span`
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const NsiCountLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NsiDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
  max-width: 640px;
`;

/** Сообщение результата импорта (успех/ошибка) — мягкий блок под полем загрузки. */
const NsiResult = styled.div<{ $tone: 'success' | 'danger' }>`
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme, $tone }) => theme.colors.status[$tone]};
  background: ${({ theme, $tone }) => `${theme.colors.status[$tone]}12`};
  border: 1px solid ${({ theme, $tone }) => `${theme.colors.status[$tone]}33`};
`;

const NsiBusy = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

/* ── Просмотр и поиск позиций справочника ──────────────────────────────────── */

const BrowseWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing(3)};
`;

const BrowseToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-self: flex-start;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  padding: ${({ theme }) => `${theme.spacing(1)} ${theme.spacing(1.5)}`};
  margin-left: ${({ theme }) => `-${theme.spacing(1.5)}`};
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover {
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  svg {
    flex-shrink: 0;
  }
`;

const BrowseMeta = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const BrowseList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  max-height: 340px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
`;

const BrowseItem = styled.li`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(2.5)}`};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

const BrowseName = styled.span`
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const BrowseSub = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2.5)}`};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-variant-numeric: tabular-nums;
`;

const BrowseEmpty = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
`;

/**
 * Просмотр и поиск позиций справочника материалов. Свёрнут по умолчанию (кнопка
 * «Показать позиции») — раскрытие подгружает первую страницу; поле поиска (с
 * дебаунсом) фильтрует по наименованию/коду/синониму на сервере. Доступно всем с
 * правом чтения (листинг ничего не меняет). Показывает первые N; при большем
 * числе совпадений — подсказка уточнить поиск.
 */
function NomenclatureBrowser() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');

  // Дебаунс ввода — не дёргаем сервер на каждую букву.
  useEffect(() => {
    const t = setTimeout(() => setQuery(input.trim()), 300);
    return () => clearTimeout(t);
  }, [input]);

  const { data, isFetching } = useListMtoNomenclatureItemsQuery(
    { q: query || undefined, limit: 50 },
    { skip: !open },
  );
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const truncated = total > items.length;

  return (
    <BrowseWrap>
      <BrowseToggle
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
        {open ? 'Скрыть позиции' : 'Показать позиции справочника'}
      </BrowseToggle>

      {open && (
        <>
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Поиск по наименованию, коду или синониму"
            aria-label="Поиск по справочнику материалов"
          />
          <BrowseMeta>
            {isFetching
              ? 'Ищу…'
              : query
                ? `Найдено: ${total.toLocaleString('ru-RU')}`
                : `Всего позиций: ${total.toLocaleString('ru-RU')}`}
            {truncated && ` · показаны первые ${items.length}`}
          </BrowseMeta>

          {items.length === 0 && !isFetching ? (
            <BrowseEmpty>
              {query
                ? 'Ничего не найдено — уточните запрос.'
                : 'Справочник пуст — загрузите файл выше.'}
            </BrowseEmpty>
          ) : (
            <BrowseList>
              {items.map((it) => (
                <BrowseItem key={it.id}>
                  <BrowseName>{it.name}</BrowseName>
                  <BrowseSub>
                    {it.code ? <span>код: {it.code}</span> : null}
                    {it.unit ? <span>ед.: {it.unit}</span> : null}
                    {it.category ? <span>{it.category}</span> : null}
                  </BrowseSub>
                </BrowseItem>
              ))}
            </BrowseList>
          )}

          {truncated && (
            <BrowseMeta>Показаны первые {items.length} — уточните поиск, чтобы найти нужное.</BrowseMeta>
          )}
        </>
      )}
    </BrowseWrap>
  );
}

/* ── Плитка подфункции «Документы качества для АОСР» ────────────────────────── */

const FeatureTile = styled(Card).attrs({ as: Link })`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  text-decoration: none;
  color: inherit;
  cursor: pointer;

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadow.lg};
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.lg};
  }
`;

const FeatureIcon = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FeatureBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const FeatureTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FeatureDesc = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const FeatureArrow = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.primary};

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  ${FeatureTile}:hover & > svg {
    transform: translateX(3px);
  }
`;

/**
 * Панель справочника НСИ (модуль 1.3 «НСИ-сопоставление»). Показывает текущий
 * размер справочника и поле загрузки нового справочника номенклатуры (XLSX/PDF).
 * Само сопоставление позиций идёт в составе «Ведомости материалов по РД» —
 * отдельной страницы списка у модуля нет. Загрузка справочника — под правом
 * записи (`can_write`). Повторная загрузка дополняет справочник: позиции, которые
 * уже есть (по коду 1С, без кода — по наименованию), пропускаются.
 */
function NomenclaturePanel({ canWrite }: { canWrite: boolean }) {
  const { data, isLoading } = useGetMtoNomenclatureQuery();
  const [importNsi, { isLoading: importing }] = useImportMtoNomenclatureMutation();
  const [imported, setImported] = useState<{ imported: number; duplicates: number } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const count = data?.count ?? 0;

  async function handleFile(file: File) {
    setError(null);
    setImported(null);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await importNsi(form).unwrap();
      setImported(res);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Не удалось загрузить справочник. Проверьте файл.');
      // eslint-disable-next-line no-console
      console.error('importMtoNomenclature failed', err);
    }
  }

  return (
    <NsiCard>
      <NsiHead>
        <div>
          <CardTitle as="span">Справочник материалов</CardTitle>
          <NsiDescription>
            Справочник номенклатуры 1С. По нему строкам ведомости материалов подбираются коды
            1С.
          </NsiDescription>
        </div>
        <NsiCount>
          <NsiCountValue>{isLoading ? '…' : count.toLocaleString('ru-RU')}</NsiCountValue>
          <NsiCountLabel>позиций в справочнике</NsiCountLabel>
        </NsiCount>
      </NsiHead>

      {canWrite ? (
        <>
          <FileDropZone
            accept={ACCEPT_ANY}
            primary="Перетащите справочник — любой формат"
            hint="Любой формат — Excel, PDF, скан или фото. Новые позиции добавятся, уже загруженные — пропустятся."
            onFile={handleFile}
          />
          {importing && <NsiBusy>Загружаю справочник…</NsiBusy>}
          {imported !== null && (
            <NsiResult $tone="success">
              Добавлено позиций: {imported.imported.toLocaleString('ru-RU')}
              {imported.duplicates > 0
                ? `, уже были в справочнике: ${imported.duplicates.toLocaleString('ru-RU')}`
                : ''}
              .
            </NsiResult>
          )}
          {error && <NsiResult $tone="danger">{error}</NsiResult>}
        </>
      ) : (
        <NsiBusy>Загрузка справочника доступна пользователям с правом изменений.</NsiBusy>
      )}

      <NomenclatureBrowser />
    </NsiCard>
  );
}

/** Подписи метрик-заглушек. Ключи строго соответствуют контракту `MtoMetrics`,
 *  что даёт исчерпывающую типобезопасную раскладку без «сырых» строк. */
const METRIC_LABELS: Record<keyof MtoMetrics, string> = {
  runs_total: 'Обработок всего',
  passports_total: 'Паспортов качества',
  nomenclature_total: 'Позиций номенклатуры',
  open_discrepancies: 'Открытых расхождений',
};

const METRIC_ORDER: ReadonlyArray<keyof MtoMetrics> = [
  'runs_total',
  'passports_total',
  'nomenclature_total',
  'open_discrepancies',
];

/**
 * Обзор агента ОСНОВА.МТО (корень `/agents/mto`). Сверху — сводка
 * (KPI), затем справочник НСИ. Перечень модулей живёт в левом рельсе оболочки
 * (`MtoLayout`) — здесь не дублируется.
 */
export function MtoDashboard() {
  const { can_read, can_write } = useAgentPermission('mto');
  const { data: metrics } = useGetMtoMetricsQuery();

  // Мягкая проверка прав: роут уже под ProtectedShell, но дублируем заглушку,
  // как в остальных разделах, чтобы не мигало содержимым без прав.
  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «МТО».
      </FallbackBanner>
    );
  }

  return (
    <div>
      <Section>
        <SectionLabel>Сводка</SectionLabel>
        <StatsRow>
          {METRIC_ORDER.map((key, i) => (
            <StatCard key={key} $delay={i * 60}>
              <StatLabel>{METRIC_LABELS[key]}</StatLabel>
              <StatValue>{(metrics?.[key] ?? 0).toLocaleString('ru-RU')}</StatValue>
            </StatCard>
          ))}
        </StatsRow>
      </Section>

      <Section>
        <SectionLabel>Документы качества</SectionLabel>
        <FeatureTile to="/agents/mto/quality">
          <FeatureIcon>
            <IconClipboardCheck size={24} />
          </FeatureIcon>
          <FeatureBody>
            <FeatureTitle>Документы качества для АОСР</FeatureTitle>
            <FeatureDesc>
              Прослеживаемость материалов: реестр партий и их паспортов, сертификатов и деклараций,
              факты применения, проверка действительности документов на дату работ и поиск
              применённых материалов без документов.
            </FeatureDesc>
          </FeatureBody>
          <FeatureArrow>
            <IconArrowRight size={18} />
          </FeatureArrow>
        </FeatureTile>
      </Section>

      <Section>
        <SectionLabel>Справочник материалов</SectionLabel>
        <NomenclaturePanel canWrite={can_write} />
      </Section>
    </div>
  );
}
